<div class="ia-page">
    <vb-breadcrumb
        [trail]="['My invoices', 'Your order']"
        [showBack]="false"
        (crumbSelect)="goToInvoices()">
    </vb-breadcrumb>

    <div class="ia-title-row">
        <h1 class="ia-title">Your order</h1>
        <span class="ia-eyebrow">Invoice {{ invoiceNumber }}</span>
    </div>

    <div class="ia-layout">
        <!-- ============ LEFT: steps / loading / error / success ============ -->
        <div class="ia-left">
            <div class="ia-card">
                <div class="vb-spinner-box vb-spinner-box--panel" *ngIf="isLoading">
                    <vb-spinner size="xl" label="Loading your invoice"></vb-spinner>
                </div>

                <div class="ia-error" *ngIf="!isLoading && loadFailed">
                    <div class="ia-error__disc" aria-hidden="true">!</div>
                    <h3 class="ia-error__title">We couldn't load this invoice</h3>
                    <p class="ia-error__body">
                        Invoice not found or no longer available. Head back to
                        your invoices to try another.
                    </p>
                    <vb-button variant="primary" (clicked)="goToInvoices()">
                        Back to my invoices
                    </vb-button>
                </div>

                <vb-invoice-order-confirm
                    *ngIf="!isLoading && !loadFailed && paymentSuccess"
                    [paymentSuccess]="paymentSuccess"
                    [isPayOnPickup]="isPayOnPickup"
                    [invoiceDetails]="invoiceDetails"
                    [scheduleLater]="scheduleLater"
                    [selectedDeliveryMethod]="selectedDeliveryMethod"
                    [selectedDate]="selectedDate"
                    [selectedSlot]="selectedSlot"
                    [proxyName]="proxyName"
                    [proxyTelephone]="proxyTelephone"
                    [selectedShippingAddress]="selectedShippingAddress"
                    [selectedPaymentCard]="selectedPaymentCard" />

                <ng-container *ngIf="!isLoading && !loadFailed && !paymentSuccess">
                    <div class="ia-intro">
                        <h2 class="ia-intro__heading">{{ headingText }}</h2>
                        <p class="ia-intro__sub">Here are your next steps.</p>
                    </div>

                    <div class="ia-steps">
                        <vb-step-section
                            data-step="delivery"
                            [number]="stepNumbers.delivery"
                            title="Fulfillment method"
                            [summary]="deliverySummary"
                            [done]="isStepOneComplete"
                            [open]="openStep === 'delivery'"
                            [enabled]="true"
                            (headerClick)="onStepHeader('delivery')">
                            <vb-invoice-delivery-options
                                [scheduleLater]="scheduleLater"
                                [isStepOneComplete]="isStepOneComplete"
                                [selectedDeliveryMethod]="selectedDeliveryMethod"
                                [isPayOnlyMode]="isPayOnlyMode"
                                [isPickupFlagged]="isPickupFlagged()"
                                [hasPickupScheduled]="hasPickupScheduled()"
                                [invoiceDetails]="invoiceDetails"
                                (deliveryMethodSelected)="onDeliveryMethodChange($event)"
                                (stepOneComplete)="onStepOneComplete($event)"
                                (scheduleLaterToggleChanged)="toggleScheduleLater($event)"
                                (selectedSchedulerChange)="onSchedulerChange($event)"
                                (payOnlyPreviewChange)="onPayOnlyPreview($event)" />
                        </vb-step-section>

                        <vb-step-section
                            *ngIf="showFulfillmentStep"
                            data-step="fulfillment"
                            [number]="stepNumbers.fulfillment"
                            title="Fulfillment details"
                            [summary]="fulfillmentSummary"
                            [done]="isStepTwoComplete"
                            [open]="openStep === 'fulfillment'"
                            [enabled]="fulfillmentEnabled"
                            (headerClick)="onStepHeader('fulfillment')">
                            <vb-invoice-fulfillment-details
                                [scheduleLater]="scheduleLater"
                                [isStepOneComplete]="isStepOneComplete"
                                [selectedDeliveryMethod]="selectedDeliveryMethod"
                                [hasPickupScheduled]="hasPickupScheduled()"
                                [selectedShippingAddress]="selectedShippingAddress"
                                [selectedDate]="selectedDate"
                                [selectedSlot]="selectedSlot"
                                [proxyName]="proxyName"
                                [proxyTelephone]="proxyTelephone"
                                [instructions]="instructions"
                                [isStepTwoComplete]="isStepTwoComplete"
                                [invoiceDetails]="invoiceDetails"
                                [selectedScheduler]="selectedScheduler"
                                (selectedPickupSlotChange)="selectedSlot = $event"
                                (deliveryInstructionsChange)="instructions = $event"
                                (proxyNameChange)="proxyName = $event"
                                (proxyTelephoneChange)="onProxyTelephoneChange($event)"
                                (selectedShippingAddressChange)="selectedShippingAddress = $event"
                                (isStepTwoCompleteChange)="onFulfillmentComplete()"
                                (selectedDateChange)="selectedDate = $event" />
                        </vb-step-section>

                        <vb-step-section
                            *ngIf="showPaymentStep"
                            data-step="payment"
                            [number]="stepNumbers.payment"
                            title="Payment"
                            [summary]="paymentSummary"
                            [done]="isStepThreeComplete"
                            [open]="openStep === 'payment'"
                            [enabled]="paymentEnabled"
                            (headerClick)="onStepHeader('payment')">
                            <vb-invoice-payment
                                [scheduleLater]="scheduleLater"
                                [isStepTwoComplete]="isStepTwoComplete"
                                [isStepThreeComplete]="isStepThreeComplete"
                                [selectedDeliveryMethod]="selectedDeliveryMethod"
                                [isPayOnPickup]="isPayOnPickup"
                                [selectedPaymentCard]="selectedPaymentCard"
                                (selectedPaymentCardChange)="selectedPaymentCard = $event"
                                (isStepThreeCompleteChange)="onPaymentComplete($event)"
                                (isPayOnPickupChange)="isPayOnPickup = $event" />
                        </vb-step-section>

                        <vb-step-section
                            data-step="confirmation"
                            [number]="stepNumbers.confirmation"
                            title="Confirmation"
                            [done]="false"
                            [open]="openStep === 'confirmation'"
                            [enabled]="confirmationEnabled"
                            (headerClick)="onStepHeader('confirmation')">
                            <vb-invoice-confirmation
                                [isStepTwoComplete]="isStepTwoComplete"
                                [isStepThreeComplete]="isStepThreeComplete"
                                [invoiceDetails]="invoiceDetails"
                                [selectedDeliveryMethod]="selectedDeliveryMethod"
                                [selectedDate]="selectedDate"
                                [selectedSlot]="selectedSlot"
                                [proxyName]="proxyName"
                                [proxyTelephone]="proxyTelephone"
                                [selectedShippingAddress]="selectedShippingAddress"
                                [selectedPaymentCard]="selectedPaymentCard"
                                [isPayOnPickup]="isPayOnPickup"
                                [scheduleLater]="scheduleLater" />
                        </vb-step-section>
                    </div>
                </ng-container>
            </div>
        </div>

        <!-- ============ RIGHT: order summary rail ============ -->
        <aside class="ia-right" *ngIf="invoiceDetails && !loadFailed">
            <vb-invoice-summary
                [invoiceDetails]="invoiceDetails"
                [paymentSuccess]="paymentSuccess"
                [isLoading]="isLoading"
                [chipLabel]="statusChipLabel"
                [chipTone]="statusChipTone"
                [confirmEnabled]="confirmReady"
                [confirmLabel]="confirmLabel"
                [showShippingTbd]="selectedDeliveryMethod === 'shipping' && isStepTwoComplete"
                (confirm)="confirmPayment()" />
        </aside>
    </div>
</div>

<!-- Mobile sticky confirm bar — the rail button's stand-in below the
     stacking breakpoint (hidden on desktop via CSS). -->
<div
    class="ia-mobilebar"
    *ngIf="!isLoading && !loadFailed && !paymentSuccess">
    <span class="ia-mobilebar__due">
        <span class="ia-mobilebar__label">Amount due</span>
        <span class="ia-mobilebar__amount">${{ amountDue | number: '1.2-2' }}</span>
    </span>
    <vb-button
        class="ia-mobilebar__btn"
        variant="primary"
        [fullWidth]="true"
        [disabled]="!confirmReady"
        (clicked)="confirmPayment()">
        {{ confirmLabel }}
    </vb-button>
</div>
